import { useState } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { FormPageLayout } from '@/components/layouts';
import { useIsFieldHidden } from '@/hooks/usePermissions';
import { DEFAULT_ESTIMATE_LIST_FILTERS } from '@/components/organisms/estimates/support/estimate-list-filters';
import { dateKeyInTimeZone } from '@/lib/date-values';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { estimateAnalyticsReportEndpoints } from '@/lib/api/endpoints';
import { estimateKeys } from '@/lib/queryKeys';
import { exportRowsAsXLSX } from '@/lib/export';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { formatEstimateSource } from '@/lib/constants/estimate-sales-attribution';
import {
  AnalyticsReportShell,
  BreakdownTable,
  MetricCard,
  ReportBarChart,
  ReportDateRangeControl,
  ReportLineChart,
  ReportSection,
  ReportTeamFilter,
  ScorecardTable,
  computeDeltaPct,
  formatCurrency,
  formatInteger,
  formatPercent,
  useDrillToList,
  useReportPeriod,
  type ScorecardColumn,
  type TeamFilterOption,
} from '@/components/organisms/analytics-reports';

// ─── Types (mirror of the backend shape) ──────────────────────────────────────

interface EstimateOverview {
  total: number;
  sent: number;
  approved: number;
  winRate: number;
  totalValue: number;
  approvedValue: number;
  avgValue: number;
}
interface BySalespersonRow {
  salespersonId: string;
  salespersonName: string;
  count: number;
  sent: number;
  approved: number;
  winRate: number;
  value: number;
  avgValue: number;
}
interface BySourceRow {
  source: string;
  count: number;
  approved: number;
  winRate: number;
  value: number;
}
interface ByChannelRow {
  channel: string;
  count: number;
  approved: number;
  winRate: number;
  value: number;
}
interface ByTechRow {
  techId: string;
  techName: string;
  count: number;
  value: number;
}
interface ApprovalsTrendPoint {
  period: string;
  value: number;
}
interface EstimateSalesSummary {
  overview: EstimateOverview;
  overviewPrevious: EstimateOverview;
  bySalesperson: BySalespersonRow[];
  bySource: BySourceRow[];
  byChannel: ByChannelRow[];
  byTech: ByTechRow[];
  approvalsTrend: ApprovalsTrendPoint[];
  teamOptions: TeamFilterOption[];
}

export const Route = createFileRoute('/_dashboard/dashboard/estimates/reports')({
  component: EstimateReportsRoute,
});

function EstimateReportsRoute() {
  const router = useRouter();
  const pricingHidden = useIsFieldHidden('PRICING');
  if (!pricingHidden) return <EstimateReportsPage />;

  return (
    <FormPageLayout
      title="Estimate Reports"
      onBack={() =>
        void router.navigate({
          to: '/dashboard/estimates',
          search: DEFAULT_ESTIMATE_LIST_FILTERS,
        })
      }
    >
      <div className="mx-auto max-w-xl px-4 py-6">
        <div className="rounded-lg border bg-muted/40 p-4">
          <p className="font-medium">Financial reports are hidden for your role.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Estimate status remains available from the Estimates list.
          </p>
        </div>
      </div>
    </FormPageLayout>
  );
}

const salespersonColumns: ScorecardColumn<BySalespersonRow>[] = [
  { key: 'name', header: 'Salesperson', type: 'text', value: (r) => r.salespersonName },
  {
    key: 'count',
    header: 'Estimates',
    type: 'number',
    value: (r) => r.count,
    total: (rows) => rows.reduce((s, r) => s + r.count, 0),
  },
  {
    key: 'sent',
    header: 'Sent',
    type: 'number',
    value: (r) => r.sent,
    total: (rows) => rows.reduce((s, r) => s + r.sent, 0),
  },
  {
    key: 'approved',
    header: 'Won',
    type: 'number',
    value: (r) => r.approved,
    total: (rows) => rows.reduce((s, r) => s + r.approved, 0),
  },
  { key: 'winRate', header: 'Win Rate', type: 'percent', value: (r) => r.winRate },
  {
    key: 'value',
    header: 'Sold $',
    type: 'currency',
    value: (r) => r.value,
    total: (rows) => rows.reduce((s, r) => s + r.value, 0),
  },
  { key: 'avgValue', header: 'Avg $', type: 'currency', value: (r) => r.avgValue },
];

function EstimateReportsPage() {
  const period = useReportPeriod('analytics.estimates.reportPeriod');
  const companyTz = useCompanyTimezone();
  const [ownerId, setOwnerId] = useState<string | undefined>(undefined);
  const params = { from: period.from, to: period.to, ownerId };
  const drillTo = useDrillToList();

  const q = useApiQuery<EstimateSalesSummary>({
    queryKey: estimateKeys.report('sales-summary', params),
    queryFn: () =>
      apiGet<EstimateSalesSummary>(
        operationsClient,
        estimateAnalyticsReportEndpoints.salesSummary(params),
      ),
    showErrorToast: false,
  });
  const data = q.data;
  const overview = data?.overview;
  const prev = data?.overviewPrevious;

  async function handleExport() {
    if (!data) return;
    const rows = data.bySalesperson.map((r) => ({
      Salesperson: r.salespersonName,
      Estimates: r.count,
      Sent: r.sent,
      Won: r.approved,
      'Win Rate': formatPercent(r.winRate),
      'Sold $': r.value,
      'Avg $': r.avgValue,
    }));
    const fname = `estimates-report-${dateKeyInTimeZone(period.from, companyTz)}_${dateKeyInTimeZone(period.to, companyTz)}.xlsx`;
    await exportRowsAsXLSX(rows, fname, { currencyColumns: ['Sold $', 'Avg $'] });
  }

  return (
    <AnalyticsReportShell
      title="Estimate Reports"
      subtitle="Sales performance and analytics"
      backTo="/dashboard/estimates"
      onExport={handleExport}
      exportDisabled={!data}
      actions={
        <>
          <ReportTeamFilter
            value={ownerId}
            onChange={setOwnerId}
            options={data?.teamOptions ?? []}
            allLabel="All salespeople"
          />
          <ReportDateRangeControl period={period} />
        </>
      }
    >
      {/* Overview */}
      <ReportSection title="Overview">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <MetricCard
            title="Total Estimates"
            value={overview ? formatInteger(overview.total) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(overview?.total, prev?.total)}
          />
          <MetricCard
            title="Sent"
            value={overview ? formatInteger(overview.sent) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(overview?.sent, prev?.sent)}
          />
          <MetricCard
            title="Won"
            value={overview ? formatInteger(overview.approved) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(overview?.approved, prev?.approved)}
          />
          <MetricCard
            title="Win Rate"
            value={overview ? formatPercent(overview.winRate) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(overview?.winRate, prev?.winRate)}
          />
          <MetricCard
            title="Total Value"
            value={overview ? formatCurrency(overview.totalValue) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(overview?.totalValue, prev?.totalValue)}
          />
          <MetricCard
            title="Avg Value"
            value={overview ? formatCurrency(overview.avgValue) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(overview?.avgValue, prev?.avgValue)}
          />
        </div>
      </ReportSection>

      {/* By salesperson */}
      <ReportSection title="By salesperson">
        <ScorecardTable<BySalespersonRow>
          rows={data?.bySalesperson ?? []}
          isLoading={q.isLoading}
          emptyMessage="No estimates attributed to a salesperson yet"
          initialSortKey="value"
          columns={salespersonColumns}
          onRowClick={(r) => drillTo('estimates', { salespersonId: r.salespersonId })}
        />
      </ReportSection>

      {/* By source */}
      <ReportSection title="By source">
        <ReportBarChart
          title="Estimates by source"
          isLoading={q.isLoading}
          data={(data?.bySource ?? []).map((s) => ({
            label: formatEstimateSource(s.source),
            value: s.count,
          }))}
        />
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Source detail</CardTitle>
          </CardHeader>
          <CardContent>
            <BreakdownTable
              rows={data?.bySource ?? []}
              isLoading={q.isLoading}
              emptyMessage="No source data available"
              columns={[
                {
                  key: 'source',
                  header: 'Source',
                  align: 'left',
                  render: (r) => formatEstimateSource(r.source),
                },
                {
                  key: 'count',
                  header: 'Estimates',
                  align: 'right',
                  render: (r) => formatInteger(r.count),
                },
                {
                  key: 'winRate',
                  header: 'Win Rate',
                  align: 'right',
                  render: (r) => formatPercent(r.winRate),
                },
                {
                  key: 'value',
                  header: 'Value',
                  align: 'right',
                  render: (r) => formatCurrency(r.value),
                },
              ]}
            />
          </CardContent>
        </Card>
      </ReportSection>

      {/* By channel */}
      <ReportSection title="By channel">
        <ReportBarChart
          title="Estimates by channel"
          isLoading={q.isLoading}
          data={(data?.byChannel ?? []).map((c) => ({ label: c.channel, value: c.count }))}
        />
      </ReportSection>

      {/* By tech */}
      <ReportSection title="By tech">
        <BreakdownTable
          rows={data?.byTech ?? []}
          isLoading={q.isLoading}
          emptyMessage="No tech-attributed estimates yet"
          columns={[
            { key: 'tech', header: 'Tech', align: 'left', render: (r) => r.techName },
            {
              key: 'count',
              header: 'Estimates',
              align: 'right',
              render: (r) => formatInteger(r.count),
            },
            {
              key: 'value',
              header: 'Value',
              align: 'right',
              render: (r) => formatCurrency(r.value),
            },
          ]}
        />
      </ReportSection>

      {/* Approvals over time */}
      <ReportSection title="Approvals over time">
        <ReportLineChart
          title="Approved estimates per month"
          isLoading={q.isLoading}
          points={(data?.approvalsTrend ?? []).map((p) => ({ period: p.period, value: p.value }))}
        />
      </ReportSection>
    </AnalyticsReportShell>
  );
}
